/**
 * Time calculation utilities for timetable generation
 * Adapted from CirculatorWebpage for Atlas
 */
import type { FixedRoute, FixedRouteStop } from '../../types/fixedRoutes';

export interface TimetableEntry {
  stopId: string;
  stopName: string;
  arrivalTime: string; // HH:mm format
  departureTime: string; // HH:mm format
  loopNumber: number;
  vehicleIndex?: number; // Index of vehicle (0-based) for multi-vehicle circulator routes
}

export interface Timetable {
  routeId: string;
  routeName: string;
  timetableEntries: TimetableEntry[];
}

/**
 * Add minutes to a time string (HH:mm format)
 * Rounds to the nearest second to avoid decimal precision issues
 */
export function addMinutesToTime(time: string, minutes: number): string {
  const [hours, mins] = time.split(':').map(Number);
  // Convert to total seconds, add minutes (as seconds), then round to nearest second
  const totalSeconds = Math.round((hours * 3600) + (mins * 60) + (minutes * 60));
  const newHours = Math.floor(totalSeconds / 3600) % 24;
  const newMins = Math.floor((totalSeconds % 3600) / 60);

  return `${String(newHours).padStart(2, '0')}:${String(newMins).padStart(2, '0')}`;
}

/**
 * Compare two time strings (HH:mm format)
 */
export function compareTimes(time1: string, time2: string): number {
  const [h1, m1] = time1.split(':').map(Number);
  const [h2, m2] = time2.split(':').map(Number);
  const minutes1 = h1 * 60 + m1;
  const minutes2 = h2 * 60 + m2;

  if (minutes1 < minutes2) return -1;
  if (minutes1 > minutes2) return 1;
  return 0;
}

/**
 * Convert HH:mm time string to total minutes since midnight
 */
function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

function getServiceWindow(route: FixedRoute, fallbackEndHour: number): { startMinutes: number; endMinutes: number } {
  const startMinutes = timeToMinutes(route.startTime);
  let endMinutes = route.endTime ? timeToMinutes(route.endTime) : fallbackEndHour * 60;

  if (endMinutes <= startMinutes) {
    endMinutes += 24 * 60;
  }

  return { startMinutes, endMinutes };
}

function timeToServiceWindowMinutes(time: string, serviceStartMinutes: number, serviceEndMinutes: number): number {
  let minutes = timeToMinutes(time);
  if (serviceEndMinutes > 24 * 60 && minutes < serviceStartMinutes) {
    minutes += 24 * 60;
  }
  return minutes;
}

/**
 * Generate timetable entries for a single loop of a route
 */
function generateRouteLoop(
  route: FixedRoute,
  startTime: string,
  loopNumber: number,
  trafficMultiplier: number,
  allStopNames: Map<string, string>,
  vehicleIndex?: number
): TimetableEntry[] {
  const entries: TimetableEntry[] = [];
  let currentTime = startTime;

  for (let i = 0; i < route.stops.length; i++) {
    const stopConfig = route.stops[i];
    const stopName = allStopNames.get(stopConfig.stopId) || stopConfig.stopId;

    // Calculate arrival time
    if (i > 0) {
      const previousStopId = route.stops[i - 1].stopId;
      const currentStopId = stopConfig.stopId;
      const segment = route.segments.find(
        (seg) => seg.fromStopId === previousStopId && seg.toStopId === currentStopId
      );
      
      if (segment) {
        const adjustedTravelTime = Math.round(segment.travelTimeMinutes * trafficMultiplier);
        currentTime = addMinutesToTime(currentTime, adjustedTravelTime);
      }
    }

    const arrivalTime = currentTime;
    const departureTime = addMinutesToTime(arrivalTime, stopConfig.waitTimeMinutes);

    entries.push({
      stopId: stopConfig.stopId,
      stopName,
      arrivalTime,
      departureTime,
      loopNumber,
      vehicleIndex,
    });

    currentTime = departureTime;
  }

  return entries;
}

function findSegmentTravelTime(
  route: FixedRoute,
  fromStopId: string,
  toStopId: string,
  trafficMultiplier: number
): number | null {
  const directSegment = route.segments.find(
    (seg) => seg.fromStopId === fromStopId && seg.toStopId === toStopId
  );
  const reverseSegment = route.segments.find(
    (seg) => seg.fromStopId === toStopId && seg.toStopId === fromStopId
  );
  const segment = directSegment ?? reverseSegment;

  return segment ? Math.round(segment.travelTimeMinutes * trafficMultiplier) : null;
}

function generateReverseReturnJourney(
  route: FixedRoute,
  startTime: string,
  loopNumber: number,
  trafficMultiplier: number,
  allStopNames: Map<string, string>,
  vehicleIndex?: number
): TimetableEntry[] {
  if (route.stops.length < 2) return [];

  const entries: TimetableEntry[] = [];
  let currentTime = startTime;

  for (let i = route.stops.length - 1; i >= 0; i--) {
    const stopConfig = route.stops[i];
    const stopName = allStopNames.get(stopConfig.stopId) || stopConfig.stopId;

    if (i < route.stops.length - 1) {
      const previousStopId = route.stops[i + 1].stopId;
      const currentStopId = stopConfig.stopId;
      const travelTime = findSegmentTravelTime(route, previousStopId, currentStopId, trafficMultiplier);

      if (travelTime !== null) {
        currentTime = addMinutesToTime(currentTime, travelTime);
      }
    }

    const arrivalTime = currentTime;
    const departureTime = addMinutesToTime(arrivalTime, stopConfig.waitTimeMinutes);

    entries.push({
      stopId: stopConfig.stopId,
      stopName,
      arrivalTime,
      departureTime,
      loopNumber,
      vehicleIndex,
    });

    currentTime = departureTime;
  }

  return entries;
}

/**
 * Generate complete timetable for a route
 */
export function generateTimetable(
  route: FixedRoute,
  trafficMultiplier: number,
  allStopNames: Map<string, string>,
  startHour: number = 8,
  endHour: number = 22
): Timetable {
  const entries: TimetableEntry[] = [];
  let currentStartTime = route.startTime;
  
  const { startMinutes: serviceStartMinutes, endMinutes: endTimeMinutes } = getServiceWindow(route, endHour);

  if (route.simulationImport?.source === 'simulation_results') {
    return {
      routeId: route.id,
      routeName: route.name,
      timetableEntries: generateRouteLoop(route, route.startTime, 1, trafficMultiplier, allStopNames, 0),
    };
  }

  // Calculate loop duration
  const calculateLoopDuration = (): number => {
    let total = 0;

    // Add all travel times (with multiplier)
    // For circulator routes, exclude the return segment here since we'll add it separately
    route.segments.forEach((seg) => {
      // Check if this is a return segment (from last stop to first stop)
      const isReturnSegment = route.isCirculator && 
        seg.fromStopId === route.stops[route.stops.length - 1]?.stopId &&
        seg.toStopId === route.stops[0]?.stopId;
      
      // Only count non-return segments here (return segment will be added separately)
      if (!isReturnSegment) {
        total += Math.round(seg.travelTimeMinutes * trafficMultiplier);
      }
    });

    // Add all wait times
    route.stops.forEach((stop) => {
      total += stop.waitTimeMinutes;
    });

    // For circulator routes: add return travel from last stop to first stop
    // This ensures the return segment is counted exactly once
    if (route.isCirculator && route.stops.length > 1) {
      const lastStopId = route.stops[route.stops.length - 1].stopId;
      const firstStopId = route.stops[0].stopId;
      const returnSegment = route.segments.find(
        (seg) => seg.fromStopId === lastStopId && seg.toStopId === firstStopId
      );

      if (returnSegment) {
        total += Math.round(returnSegment.travelTimeMinutes * trafficMultiplier);
      } else if (route.segments.length > 0) {
        // Fallback: use first segment time (assume symmetric route)
        total += Math.round(route.segments[0].travelTimeMinutes * trafficMultiplier);
      }
    }

    return total;
  };

  const loopDuration = calculateLoopDuration();

  // Check if this is a multi-vehicle circulator route
  const vehicleCount = route.isCirculator && route.vehicleCount && route.vehicleCount > 1 
    ? route.vehicleCount 
    : 1;

  // Calculate headway for multi-vehicle circulators
  const headwayMinutes = vehicleCount > 1 && loopDuration > 0
    ? loopDuration / vehicleCount
    : null;

  // Calculate trip duration for non-circulator routes (one-way trip)
  const calculateOneWayTripDuration = (): number => {
    let total = 0;
    
    // Add all travel times (with multiplier) for the forward trip
    route.segments.forEach((seg) => {
      total += Math.round(seg.travelTimeMinutes * trafficMultiplier);
    });

    // Add all wait times
    route.stops.forEach((stop) => {
      total += stop.waitTimeMinutes;
    });

    return total;
  };

  // Calculate return travel time for non-circulator routes
  const calculateReturnTravelTime = (): number => {
    // Try to find a return segment (from last stop to first stop)
    if (route.stops.length > 1) {
      const lastStopId = route.stops[route.stops.length - 1].stopId;
      const firstStopId = route.stops[0].stopId;
      const returnSegment = route.segments.find(
        (seg) => seg.fromStopId === lastStopId && seg.toStopId === firstStopId
      );
      
      if (returnSegment) {
        return Math.round(returnSegment.travelTimeMinutes * trafficMultiplier);
      }
    }
    
    // Fallback: estimate return time as 60% of forward trip time (assuming faster return)
    // Or use the sum of all segments in reverse (if we had reverse segments)
    // For now, use a reasonable estimate: 50% of total forward travel time
    const forwardTravelTime = route.segments.reduce((sum, seg) => 
      sum + Math.round(seg.travelTimeMinutes * trafficMultiplier), 0
    );
    return Math.round(forwardTravelTime * 0.5);
  };

  if (route.isCirculator && vehicleCount > 1 && headwayMinutes !== null) {
    // Multi-vehicle circulator: generate staggered loops for each vehicle
    for (let vehicleIndex = 0; vehicleIndex < vehicleCount; vehicleIndex++) {
      // Calculate staggered start time for this vehicle
      const vehicleStartOffset = vehicleIndex * headwayMinutes;
      let vehicleStartTime = addMinutesToTime(route.startTime, vehicleStartOffset);
      let loopNumber = 1;
      let currentStartTime = vehicleStartTime;

      // Generate loops for this vehicle until we exceed end time
      while (true) {
        const loopEntries = generateRouteLoop(
          route,
          currentStartTime,
          loopNumber,
          trafficMultiplier,
          allStopNames,
          vehicleIndex
        );

        if (loopEntries.length === 0) break;

        const firstArrivalMinutes = timeToServiceWindowMinutes(
          loopEntries[0].arrivalTime,
          serviceStartMinutes,
          endTimeMinutes
        );
        
        // Check if this trip starts before the end time
        if (firstArrivalMinutes >= endTimeMinutes) {
          break;
        }

        entries.push(...loopEntries);

        // Calculate when the next loop should start for this vehicle
        const lastEntry = loopEntries[loopEntries.length - 1];
        const lastDepartureTime = lastEntry.departureTime;
        
        // For circulator routes: next loop starts after return segment completes
        const lastStopId = route.stops[route.stops.length - 1].stopId;
        const firstStopId = route.stops[0].stopId;
        const returnSegment = route.segments.find(
          (seg) => seg.fromStopId === lastStopId && seg.toStopId === firstStopId
        );
        
        let returnTravelTime = 0;
        if (returnSegment) {
          returnTravelTime = Math.round(returnSegment.travelTimeMinutes * trafficMultiplier);
        } else if (route.segments.length > 0) {
          // Fallback: use first segment time
          returnTravelTime = Math.round(route.segments[0].travelTimeMinutes * trafficMultiplier);
        }
        
        // Next loop starts after return travel completes
        currentStartTime = addMinutesToTime(lastDepartureTime, returnTravelTime);
        loopNumber++;

        // Safety limit to prevent infinite loops
        if (loopNumber > 200) break;
      }
    }
  } else {
    // Single vehicle or non-circulator: generate sequential loops as before
    let currentStartTime = route.startTime;
    let loopNumber = 1;

    while (true) {
      const loopEntries = generateRouteLoop(
        route,
        currentStartTime,
        loopNumber,
        trafficMultiplier,
        allStopNames,
        0 // Single vehicle = index 0
      );

      if (loopEntries.length === 0) break;

      const firstArrivalMinutes = timeToServiceWindowMinutes(
        loopEntries[0].arrivalTime,
        serviceStartMinutes,
        endTimeMinutes
      );
      
      // Check if this trip starts before the end time
      if (firstArrivalMinutes >= endTimeMinutes) {
        break;
      }

      entries.push(...loopEntries);

      // Calculate when the next trip/loop should start
      const lastEntry = loopEntries[loopEntries.length - 1];
      const lastDepartureTime = lastEntry.departureTime;
      
      if (route.isCirculator) {
        // For circulator routes: next loop starts after return segment completes
        const lastStopId = route.stops[route.stops.length - 1].stopId;
        const firstStopId = route.stops[0].stopId;
        const returnSegment = route.segments.find(
          (seg) => seg.fromStopId === lastStopId && seg.toStopId === firstStopId
        );
        
        let returnTravelTime = 0;
        if (returnSegment) {
          returnTravelTime = Math.round(returnSegment.travelTimeMinutes * trafficMultiplier);
        } else if (route.segments.length > 0) {
          // Fallback: use first segment time
          returnTravelTime = Math.round(route.segments[0].travelTimeMinutes * trafficMultiplier);
        }
        
        // Next loop starts after return travel completes
        currentStartTime = addMinutesToTime(lastDepartureTime, returnTravelTime);
      } else {
        const returnEntries = route.reverseReturnJourney
          ? generateReverseReturnJourney(
              route,
              lastDepartureTime,
              loopNumber,
              trafficMultiplier,
              allStopNames,
              0
            )
          : [];
        const layoverTime = 5; // 5 minutes layover at the start before next trip
        const lastReturnEntry = returnEntries[returnEntries.length - 1];
        const returnCompleteTime = lastReturnEntry
          ? lastReturnEntry.departureTime
          : addMinutesToTime(lastDepartureTime, calculateReturnTravelTime());
        const nextTripStartTime = addMinutesToTime(returnCompleteTime, layoverTime);

        entries.push(...returnEntries);
        
        // Check if next trip would start before end time
        const nextTripStartMinutes = timeToServiceWindowMinutes(
          nextTripStartTime,
          serviceStartMinutes,
          endTimeMinutes
        );
        if (nextTripStartMinutes >= endTimeMinutes) {
          break;
        }
        
        currentStartTime = nextTripStartTime;
      }
      
      loopNumber++;

      // Safety limit to prevent infinite loops
      if (loopNumber > 200) break;
    }
  }

  // Sort entries by arrival time
  entries.sort(
    (a, b) =>
      timeToServiceWindowMinutes(a.arrivalTime, serviceStartMinutes, endTimeMinutes) -
      timeToServiceWindowMinutes(b.arrivalTime, serviceStartMinutes, endTimeMinutes)
  );

  return {
    routeId: route.id,
    routeName: route.name,
    timetableEntries: entries,
  };
}
